import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  Link as LinkIcon,
  AlertTriangle,
  Settings,
  Home,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import Logo from '../../components/Logo/Logo';


const STATUS = Object.freeze({
  SUCCESS: 'success',
  INVALID: 'invalid',
});

const CATEGORY_LABELS = Object.freeze({
  postExpiringWarning: {
    key: 'unsubscribe_category_post_expiring_warning',
    defaultValue: 'Post expiring reminders',
  },
  postExpiredNotice: {
    key: 'unsubscribe_category_post_expired_notice',
    defaultValue: 'Post expired notices',
  },
  marketing: {
    key: 'unsubscribe_category_marketing',
    defaultValue: 'Updates and announcements',
  },
  all: {
    key: 'unsubscribe_category_all',
    defaultValue: 'All emails from All Animal App',
  },
});

const TONE_CLASSES = {
  success: {
    iconBg: 'bg-green-100',
    iconColor: 'text-green-700',
    accent: 'text-green-900',
  },
  error: {
    iconBg: 'bg-red-100',
    iconColor: 'text-red-700',
    accent: 'text-red-900',
  },
  neutral: {
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-700',
    accent: 'text-gray-900',
  },
};

function getCategoryLabel(category, t) {
  const entry = CATEGORY_LABELS[category];
  if (!entry) return null;
  return t(entry.key, entry.defaultValue);
}

const EmailUnsubscribe = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const rawStatus = (searchParams.get('status') || '').toLowerCase();
  const rawCategory = searchParams.get('category') || '';

  const categoryLabel = useMemo(
    () => getCategoryLabel(rawCategory, t),
    [rawCategory, t],
  );

  const content = useMemo(() => {
    if (rawStatus === STATUS.SUCCESS) {
      const isAll = rawCategory === 'all';

      let body;
      if (isAll) {
        body = t(
          'unsubscribe_success_all_body',
          "You've been unsubscribed from all emails from All Animal App. You can re-enable specific notifications any time from Settings.",
        );
      } else if (categoryLabel) {
        body = t(
          'unsubscribe_success_category_body',
          "You've been unsubscribed from {{category}}. To manage all your email preferences, log in and visit Settings → Email Notifications.",
          { category: categoryLabel },
        );
      } else {
        body = t(
          'unsubscribe_success_generic_body',
          "You've been unsubscribed. To manage all your email preferences, log in and visit Settings → Email Notifications.",
        );
      }

      return {
        tone: 'success',
        Icon: CheckCircle2,
        title: t('unsubscribe_success_title', "You're unsubscribed"),
        body,
        primaryCta: isAuthenticated
          ? t('unsubscribe_manage_prefs_cta', 'Manage all preferences')
          : t('unsubscribe_open_app_cta', 'Open the app'),
        primaryAction: isAuthenticated ? 'profile' : 'home',
      };
    }

    if (rawStatus === STATUS.INVALID) {
      return {
        tone: 'error',
        Icon: LinkIcon,
        title: t('unsubscribe_invalid_title', 'This unsubscribe link is invalid'),
        body: t(
          'unsubscribe_invalid_body',
          "We couldn't process this unsubscribe link. It may have been copied incompletely. To opt out, log in and update your email preferences from Settings.",
        ),
        primaryCta: isAuthenticated
          ? t('unsubscribe_manage_prefs_cta', 'Manage all preferences')
          : t('unsubscribe_open_app_cta', 'Open the app'),
        primaryAction: isAuthenticated ? 'profile' : 'home',
      };
    }

    return {
      tone: 'neutral',
      Icon: AlertTriangle,
      title: t('unsubscribe_unknown_title', 'Something went wrong'),
      body: t(
        'unsubscribe_unknown_body',
        "We didn't recognise this confirmation. To manage your email preferences, log in and visit Settings → Email Notifications.",
      ),
      primaryCta: isAuthenticated
        ? t('unsubscribe_manage_prefs_cta', 'Manage all preferences')
        : t('unsubscribe_open_app_cta', 'Open the app'),
      primaryAction: isAuthenticated ? 'profile' : 'home',
    };
  }, [rawStatus, rawCategory, categoryLabel, isAuthenticated, t]);

  const tone = TONE_CLASSES[content.tone] || TONE_CLASSES.neutral;
  const { Icon } = content;
  const PrimaryIcon = content.primaryAction === 'profile' ? Settings : Home;

  const handlePrimary = () => {
    if (content.primaryAction === 'profile') {
      navigate('/profile');
      return;
    }
    navigate('/');
  };

  return (
    <motion.div
      className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      role="main"
      aria-labelledby="email-unsubscribe-title"
    >
      <div className="max-w-md w-full text-center space-y-6">
        <motion.div
          className="flex flex-col items-center"
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
        >

          <div className="relative">
            <Logo
              className="w-28 h-28 sm:w-32 sm:h-32 drop-shadow"
              tmClassName="-top-px right-1 text-[0.45rem]"
            />
            <span
              className={[
                'absolute -bottom-1 -right-1 w-10 h-10 sm:w-11 sm:h-11 rounded-full ring-4 ring-background flex items-center justify-center',
                tone.iconBg,
              ].join(' ')}
              aria-hidden="true"
            >
              <Icon className={['w-5 h-5 sm:w-6 sm:h-6', tone.iconColor].join(' ')} />
            </span>
          </div>
        </motion.div>

        <motion.h1
          id="email-unsubscribe-title"
          className={['text-2xl sm:text-3xl font-bold', tone.accent].join(' ')}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {content.title}
        </motion.h1>

        <motion.p
          className="text-sm sm:text-base text-muted-foreground leading-relaxed"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {content.body}
        </motion.p>

        <motion.div
          className="flex flex-col gap-3 pt-2"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            onClick={handlePrimary}
            className="h-11 px-6 w-full flex items-center justify-center gap-2"
          >
            <PrimaryIcon className="w-4 h-4" aria-hidden="true" />
            {content.primaryCta}
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default EmailUnsubscribe;
